export default Kinetics;

import hrtime from '../utils/hrtime.js';
import nearestIndex from '../utils/nearestIndex.js';
import Ease from '../anim/ease.js';

const transition = 500;

function Kinetics(options) {
	options = options || {};

	this._maxSamples = 3;

	this.samples = [];
}

/**
 * This will push a new sample to the Kinetics object, optionally with a given
 * timestamp, which will be the current time by default
 * @param {*} sample
 * @param {Number=} time (defaults to the current time)
 */
Kinetics.prototype.push = function (sample, time) {
	time = time || hrtime();
	this.samples.unshift({time, sample});
	if (this.samples.length > this._maxSamples) {
		this.samples.pop();
	}
	return this;
};

/**
 * Reset the points tracked so far.
 */
Kinetics.prototype.reset = function () {
	this.samples = [];
	return this;
};

/**
 * This will calculate the velocity at the given sample (the latest by default)
 * The value returned will be in `units/millisecond`.
 * @param {number=} sample (defaults to `0`, the latest sample)
 * @return {*} the velocity for each of the components of the sample data
 */
Kinetics.prototype.getVelocity = function (i, num) {
	const {samples} = this;
	i = i || 0;
	if (samples.length < 2) { return 0; }
	num = Math.min(num || samples.length - 1, samples.length - 1);
	if (i + num > samples.length - 1) { return 0; }

	const deltaTime = samples[i].time - samples[i + num].time;
	const deltaSample = samples[i].sample - samples[i + num].sample;
	return deltaSample / deltaTime;
};

/**
 * This will return the acceleration at the given sample (the latest by default)
 * The value returned will be in `units/millisecond^2`.
 * @param {number=} sample (defaults to `0`, the latest sample)
 * @return {*} the acceleration for each of the components of the sample data
 */
Kinetics.prototype.getAcceleration = function (i) {
	i = i || 0;

	if (this.samples.length < i + 3) {
		return 0;
	}

	var deltaTime = this.samples[i].time - this.samples[i + 2].time;
	var velocity = this.getVelocity(i);
	var deltaVelocity = velocity - this.getVelocity(i + 1);
	return deltaVelocity / deltaTime;
};

Kinetics.prototype.getRestingPositionDelta = function (options) {
	if (typeof options === 'number') {
		options = {threshold: options};
	}
	options = options || {};
	const minSpeed = options.threshold || 0.15;
	const friction = options.friction || 0.002;

	const velocity = this.getVelocity();
	const maxSpeed = Math.abs(velocity);

	if (maxSpeed > minSpeed) {
		// See http://metafizzy.co/blog/math-time-resting-position/
		const duration = Math.log(minSpeed / maxSpeed) / Math.log(1 - friction);
		const distanceFactor = (Math.pow(1 - friction, duration + 1) - 1) / (-friction);

		return velocity * distanceFactor;
	}
	return 0;
};

/**
 * Use the tracked kinetic events to further animate a given property of a
 * layer.
 * @param {Layer} layer
 * @param {string} property
 * @param {*} options either a minimum move threshold or an object with
 *                    `threshold` and `friction` properties.
 * @return {Animation} the started Animation object, or null if no
 *                     animation was started
 */
Kinetics.prototype.animate = function (layer, property, options) {
	if (typeof options === 'number') {
		options = {threshold: options};
	}
	options = options || {};
	var minSpeed = options.threshold || 0.15;
	var friction = options.friction || 0.002;
	var snapPoints = options.snap || [];
	var bounds = options.bounds;
	var hasSnap = snapPoints.length;
	var start = options.start;
	var fling = options.fling;

	var velocity = this.getVelocity();
	var maxSpeed = Math.abs(velocity);

	var duration, end, ease = Ease.outCube, distance = 0;
	if (maxSpeed > minSpeed) {
		// See http://metafizzy.co/blog/math-time-resting-position/
		duration = Math.log(minSpeed / maxSpeed) / Math.log(1 - friction);
		var distanceFactor = (Math.pow(1 - friction, duration + 1) - 1) / (-friction);

		distance = velocity * distanceFactor;
		end = start + distance;
	} else if (hasSnap || bounds) {
		duration = transition;
		end = start;
	} else {
		return null;
	}

	var snapped = false;

	var snapDiff = 0;
	if (hasSnap) {
		var boundSnap = (options.snapBounds) ? snapInBounds(end, options.snap, options.snapBounds) : -1;
		if (!options.snapBounds || boundSnap >= 0) {
			end = snap(snapPoints, end, fling !== undefined && maxSpeed > minSpeed * 2 ? fling : end);
			snapped = true;
			if (boundSnap >= 0) { snapDiff = snapPoints[boundSnap] - end; }
		}

		if (snapped && options.snapBounds) {
			if (snapDiff < 0) {
				end -= options.snapBounds[0];
			} else {
				end += options.snapBounds[0];
			}
		}

		if (options.snapBounds) {
			end = Math.max(options.snap[0] - options.snap[1] + options.snap[0] + options.snapBounds[1], Math.min(options.snap[options.snap.length - 1] + options.snapBounds[0], end));
		} else {
			end = Math.max(options.snap[0], Math.min(options.snap[options.snap.length - 1], end));
		}
	} else if (bounds) {
		end = Math.max(bounds[0], Math.min(end, bounds[1]));
	}

	var diff = end - start;

	if (distance && (hasSnap || bounds)) {
		// adjust the duration according to the change in distance
		let adjust = diff / distance;
		if (adjust > 0 && adjust < 1) {
			duration *= adjust;
		} else if (adjust < 0) {
			duration = transition;
		}
	}

	var animation = layer.animate(property, duration, 0, ease, start, end);

	if (Math.abs(diff) < 10) {
		// XXX: if we move less than Xpx, just make this instant, without animating
		animation.stop(true);
		return null;
	}

	return animation;
};

function snapInBounds (offset, points, bounds) {
	var out = -1;
	points.some((snap, i) => {
		var s = -(snap - offset);
		if (s >= bounds[0] && s <= bounds[1]) {
			out = i;
			return true;
		}
		return false;
	});
	return out;
}

function snap(points, to, from) {
	if (!points.length) {
		return to;
	}

	var toIndex = nearestIndex(points, to);
	var fromIndex = nearestIndex(points, from);
	toIndex = Math.max(fromIndex - 1, Math.min(toIndex, fromIndex + 1));

	return points[toIndex];
}



// WEBPACK FOOTER //
// ./lib/pagehandler/math/kinetics.js